import helpers from '@/lib/helpers'

/**
 * Uploads that go straight to Google Cloud Storage, past our proxy and its
 * request size limit - as a vue-upload-component `custom-action`, so the
 * component keeps its queue (thread), progress and cancel:
 *
 *   <file-upload :custom-action="directUpload(url)" :thread="3" multiple .../>
 *
 * Per file:
 *  1. POST the record's file url {deferred, direct, mime, filename, size, uuid}:
 *     a waiting file on the record, and where its bytes go (upload_url).
 *  2. Send the bytes there: a signed Google resumable session (PUT, no
 *     cookies) - or, with local storage (development), our own API.
 *  3. POST /api/file/{id}/complete: the file stops waiting now. Google's
 *     storage notification does the same as a backstop; whichever is first.
 *  4. (optional) finish(file): what the upload is for, once it is stored - App
 *     Distribution hands the APK to its app by id. Its answer is the response.
 *
 * Google's answer to the last PUT of a resumable session carries no CORS
 * header, so the browser reports a stored upload as a network error (the
 * console shows "net::ERR_FAILED 200 (OK)"). So a network error is not taken
 * as failure: complete is asked anyway, and the server - which looks in the
 * bucket itself - decides. Only when it finds nothing (409), or the upload was
 * cancelled, is the waiting file deleted, so nothing half-made is left.
 */

/** this browser, for the waiting file's record (the app sends its device's) */
function browserId() {
    try {
        let id = localStorage.getItem('upload_uuid')
        if (!id) {
            id = 'web-' + (crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2))
            localStorage.setItem('upload_uuid', id)
        }
        return id
    } catch (_) {
        return 'web'
    }
}

/** the bytes to upload_url, reporting progress, stopping when the component cancels the file */
function send(upload, file, component) {
    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest()
        const direct = /^https?:\/\//.test(upload.href)
        const mime = file.type || 'application/octet-stream'

        xhr.open(upload.method, upload.href)
        let body
        if (direct) {
            // Google: the raw bytes, its content type - and no cookies or API headers
            xhr.setRequestHeader('Content-Type', mime)
            body = file.file
        } else {
            // our API (local storage): one multipart file, with the session cookie
            xhr.withCredentials = true
            xhr.setRequestHeader('Accept', 'application/json')
            body = new FormData()
            body.append('file', file.file, file.name)
        }

        let last = 0
        let lastAt = Date.now()
        xhr.upload.onprogress = (e) => {
            if (!e.lengthComputable)
                return
            const now = Date.now()
            const speed = now > lastAt ? Math.round((e.loaded - last) / ((now - lastAt) / 1000)) : 0
            last = e.loaded
            lastAt = now
            component.update(file, {progress: (e.loaded / e.total * 100).toFixed(2), speed})
        }

        // the component's cancel (Remove) turns the file inactive: stop sending
        const watch = setInterval(() => {
            const f = component.get(file)
            if (!f || !f.active) {
                clearInterval(watch)
                xhr.abort()
            }
        }, 200)

        xhr.onload = () => {
            clearInterval(watch)
            if (xhr.status >= 200 && xhr.status < 300)
                resolve()
            else
                reject(new Error('Upload failed (' + xhr.status + ')'))
        }
        xhr.onerror = () => {
            clearInterval(watch)
            reject(new Error('network'))
        }
        xhr.onabort = () => {
            clearInterval(watch)
            reject(new Error('abort'))
        }

        xhr.send(body)
    })
}

export function directUpload(url, finish = null) {
    return async (file, component) => {
        // a refusal (no permission, a message's attachment limits) says why, as a toast
        const started = await helpers.asyncAction(url, 'POST', {
            noload: true,
            toast: true,
            data: {
                deferred: 1,
                direct: 1,
                mime: file.type || 'application/octet-stream',
                filename: file.name,
                // the server refuses one over its cap before a byte is sent, and holds what lands to it
                size: file.size,
                uuid: browserId(),
            },
        })
        const made = started.files[0]

        try {
            try {
                await send(made.upload_url, file, component)
            } catch (e) {
                // a cancel is a cancel; a "network error" may be Google's unreadable 200 (see above)
                if (e.message !== 'network')
                    throw e
            }
            let done = await helpers.asyncAction('/api/file/' + made.id + '/complete', 'POST', {noload: true, toast: true})
            if (finish)
                done = await finish(made)
            component.update(file, {response: done})
            return done
        } catch (e) {
            // nothing half-made left on the record
            helpers.asyncAction('/api/file/' + made.id, 'DELETE', {noload: true, quiet: true}).catch(() => {})
            throw e
        }
    }
}

export default directUpload
